import { Field, FieldProps, Formik } from "formik";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Button, CardBody, Col, Form, Label, Row } from "reactstrap";
import Select from "react-select";
import * as Yup from "yup";

import FuelEUDailyResultsModal from "./FuelEUDailyResultsModal";
import { FuelMixFields, LcvEcho, fuelMixSchema } from "./FuelEUFuelRows";
import QuantityField from "./QuantityField";
import {
  MAX_DAILY_QUANTITY_DIGITS,
  MAX_E_VALUE_DECIMALS,
  MAX_E_VALUE_DIGITS,
  MAX_LCV_DECIMALS,
  MAX_LCV_DIGITS,
  MAX_OPS_DECIMALS,
  MAX_OPS_DIGITS,
  MAX_QUANTITY_DIGITS,
  apiErrorMessage,
  errorToast,
  loadingToast,
} from "./common";
import { useIsCompact, useIsMobile } from "./useResponsive";
import {
  FuelEUTargets,
  FuelFactor,
  balanceRatingClass,
  emptyFuelRow,
  formatBalance,
  formatEuro,
  formatNumber,
  BLEND_FOSSIL_FACTOR_IDS,
  bioBlendOption,
  groupFuelFactors,
  loadFuelEUTargets,
  loadFuelFactors,
  needsOverride,
  usesEValue,
} from "./fueleu";
import {
  DAY_PRESETS,
  DEFAULT_DAYS,
  ELAPSED_PRESETS,
  FuelEUDailyResult,
  MAX_DAYS,
  MODE_FULL,
  MODE_PARTIAL,
  PLAN_PRESETS,
  buildDailyPayload,
  formatDays,
  formatRequirement,
  isPartial,
  planWindow,
  postDailyPreview,
  saveFuelEUDaily,
} from "./fueleuDaily";

/**
 * FuelEU Maritime day-by-day biofuel-requirement calculator.
 *
 * The planning half of the FuelEU tab beside it. That one scores a bunker that
 * has happened; this one takes a daily burn and a horizon and answers the
 * question a bunker plan is actually built around — how much biofuel has to be
 * aboard by day N for the balance accrued so far to be zero.
 *
 * Every figure comes from `fueleu_daily_calculator/preview/`, which runs the
 * same `ecosail/fueleu.py` as the annual calculator over the same mix scaled to
 * the horizon. 10 mt/day for 365 days and 3,650 mt entered on the other tab are
 * the same calculation and return the same balance, by construction.
 *
 * A plan need not start on day one, and the form asks which of the two
 * questions is being put to it before asking anything else:
 *
 *   MODE_FULL     nothing burnt yet — one horizon, planned end to end.
 *   MODE_PARTIAL  a stretch already burnt, then a window planned ahead of it.
 *
 * A switch rather than two sections both offering a number of days. Stacked,
 * they read as a contradiction: a "planning horizon" of 1 day above "30 days
 * already consumed" is not a plan, it is an error message waiting to happen, and
 * the reader has to work out for themselves which number contains the other.
 *
 * The API takes one horizon and an offset into it, so MODE_PARTIAL's two boxes
 * are added on the way out (`buildDailyPayload`) rather than stored apart. The
 * form asks the question the operator is actually holding — "I have burnt 90
 * days, plan me the next 90" — and the payload does the arithmetic.
 *
 * The split itself: the balance is still scored over the whole period, but only
 * the remaining days' fossil energy is available to substitute against it. So
 * the tonnage of biofuel needed stays the same and the *rate* it has to be
 * bunkered at goes up — which is the answer someone 90 days into a bad year is
 * after.
 */

/** How long the form sits still before it asks the API to recompute. */
const PREVIEW_DEBOUNCE_MS = 400;

const FuelEUDailyCalculator = () => {
  const isMobile = useIsMobile();
  const isCompact = useIsCompact();

  const [factors, setFactors] = useState<FuelFactor[]>([]);
  const [targets, setTargets] = useState<FuelEUTargets | null>(null);
  const [preview, setPreview] = useState<FuelEUDailyResult | null>(null);
  const [previewError, setPreviewError] = useState<string>("");
  const [modalState, setModalState] = useState(false);
  const [recordId, setRecordId] = useState(0);
  const [savedResult, setSavedResult] = useState<FuelEUDailyResult | null>(null);

  useEffect(() => {
    loadFuelFactors().then(setFactors);
    loadFuelEUTargets().then(setTargets);
  }, []);

  const pickerOptions = useMemo(
    () => [...factors, bioBlendOption()],
    [factors]
  );

  const factorsById = useMemo(() => {
    const map = new Map<number, FuelFactor>();
    pickerOptions.forEach((factor) => map.set(factor.id, factor));
    return map;
  }, [pickerOptions]);

  const fuelGroups = useMemo(
    () => groupFuelFactors(pickerOptions), [pickerOptions]);

  const blendFossilOptions = useMemo(
    () => factors.filter((f) => BLEND_FOSSIL_FACTOR_IDS.includes(f.id)),
    [factors]
  );

  /**
   * Only the fuels worth substituting *in*. A plan denominated in heavy fuel oil
   * would answer "how much HFO do I need to fix an HFO problem", which is a
   * question the API declines correctly and that should not be askable.
   */
  const alternativeGroups = useMemo(
    () =>
      groupFuelFactors(factors).filter((group) => group.label !== "Fossil"),
    [factors]
  );

  const yearOptions = useMemo(
    () =>
      (targets?.targets ?? []).map((target) => ({
        value: target.year,
        label: `${target.year}  —  target ${formatNumber(
          target.target_intensity,
          2
        )} gCO₂eq/MJ  (−${formatNumber(target.reduction * 100, 1)}%)`,
      })),
    [targets]
  );

  const initialValues = {
    name_of_vessel: "",
    imo_number: "",
    year: 2025,
    // Which of the two questions the form is answering. Full is the default and
    // the whole of the behaviour before the split existed.
    mode: MODE_FULL,
    // MODE_FULL: the whole horizon.
    days: DEFAULT_DAYS,
    // MODE_PARTIAL: the stretch behind, and the window ahead of it. Added
    // together into `days` by buildDailyPayload — the API takes one horizon.
    elapsed_days: 90,
    plan_days: MAX_DAYS - 90,
    consumed_fuels: [emptyFuelRow()],
    fuels: [emptyFuelRow()],
    // kWh per day, not MJ — see the OPS field. buildDailyPayload converts.
    ops_energy_kwh_per_day: 0,
    alternative_fuel: null as number | null,
    alternative_intensity_override: "",
    alternative_lcv_override: "",
    alternative_e_value: "",
  };

  const isFilled = (value: any) => String(value ?? "").trim() !== "";

  const alternativeFactorFor = (values: any) =>
    values?.alternative_fuel
      ? factorsById.get(values.alternative_fuel)
      : undefined;

  const validationSchema = Yup.object({
    name_of_vessel: Yup.string().required("This field is required!"),
    imo_number: Yup.string()
      .matches(/^\d{7}$/, "Please enter 7 digits")
      .required("This field is required!"),
    year: Yup.number().required("This field is required!"),
    // Mirrors the model's own validators rather than trusting the API to
    // reject it: a 0 here would divide every per-day average by nothing, and
    // the error should land under the box, not in a toast.
    days: Yup.number()
      .typeError("Enter a number of days")
      .integer("Whole days only")
      .min(1, "At least one day")
      .max(
        MAX_DAYS,
        `FuelEU scores the calendar year, so a plan stops at ${MAX_DAYS} days.`
      )
      .required("This field is required!"),
    // The two MODE_PARTIAL boxes. Each is checked on its own and then against
    // the other, because they have to sum to a year at most: the compliance
    // period is the calendar year, and a plan reaching past it would accrue one
    // balance across two targets. Neither rule fires in MODE_FULL, where the
    // boxes are not on screen and an error would have nowhere to land.
    elapsed_days: Yup.number()
      .typeError("Enter a number of days")
      .integer("Whole days only")
      .test(
        "elapsed-in-range",
        `Between 1 and ${MAX_DAYS - 1} days.`,
        function (value) {
          if (!isPartial(this.parent)) return true;
          return Number(value) >= 1 && Number(value) <= MAX_DAYS - 1;
        }
      ),
    plan_days: Yup.number()
      .typeError("Enter a number of days")
      .integer("Whole days only")
      .test(
        "plan-window-fits-the-year",
        "This runs past the end of the compliance year.",
        function (value) {
          if (!isPartial(this.parent)) return true;
          const elapsed = Number(this.parent?.elapsed_days) || 0;
          const plan = Number(value);
          if (!Number.isFinite(plan) || plan < 1) return false;
          return elapsed + plan <= MAX_DAYS;
        }
      ),

    // The fuel the plan is denominated in. Optional as a whole — but once
    // chosen it has to be priceable, or the plan reports "select a fuel" at a
    // fuel that is plainly selected.
    alternative_e_value: Yup.mixed().test(
      "alternative-e-required",
      "Required — Annex II derives this fuel's WtT from its E value.",
      function (value) {
        const factor = alternativeFactorFor(this.parent);
        return !usesEValue(factor) || isFilled(value);
      }
    ),
    alternative_intensity_override: Yup.mixed().test(
      "alternative-intensity-required",
      "Required — Annex II publishes no default for this fuel.",
      function (value) {
        const factor = alternativeFactorFor(this.parent);
        return (
          !factor || !needsOverride(factor) || usesEValue(factor) || isFilled(value)
        );
      }
    ),
    alternative_lcv_override: Yup.mixed().test(
      "alternative-lcv-required",
      "Required — the factor table has no LCV for this fuel.",
      function (value) {
        const factor = alternativeFactorFor(this.parent);
        return (
          !factor || !needsOverride(factor) || !!factor.lcv || isFilled(value)
        );
      }
    ),

    fuels: fuelMixSchema(factorsById),
    // Required only in MODE_PARTIAL. Otherwise the table is not on screen, and
    // demanding a row from a table nobody can see is an error with nowhere to
    // land.
    consumed_fuels: fuelMixSchema(factorsById, isPartial),
  });

  return (
    <CardBody>
      <h5 className="mt-1 mb-2">FuelEU Daily Biofuel Requirement</h5>
      <p className="fueleu-intro">
        Enter a daily burn and a planning horizon. The calculator accrues the
        compliance balance day by day and reports how much biofuel has to be
        aboard by each checkpoint to bring it back to zero.
      </p>

      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={(values: any, actions: any) => {
          actions.setSubmitting(false);
          loadingToast("Please wait! we are calculating...");
          saveFuelEUDaily(values)
            .then((data: any) => {
              setRecordId(data.id);
              setSavedResult(data.result);
              setModalState(true);
            })
            .catch((err: any) => {
              errorToast("Error! " + apiErrorMessage(err));
            });
        }}
      >
        {(props: any) => {
          const alternativeFactor = props.values.alternative_fuel
            ? factorsById.get(props.values.alternative_fuel)
            : undefined;
          // Read off the form rather than off the preview, so the sections
          // switch the moment a button is clicked rather than after the debounce
          // has fetched a plan for the new shape.
          const partial = isPartial(props.values);
          const elapsedDays = partial
            ? Math.max(0, Number(props.values.elapsed_days) || 0)
            : 0;
          const planDays = planWindow(props.values);
          const horizonDays = elapsedDays + planDays;
          const remainingDays = planDays;
          const restOfYear = Math.max(1, MAX_DAYS - elapsedDays);

          return (
            <Form onSubmit={props.handleSubmit} noValidate autoComplete="off">
              <DailyPreviewSync
                values={props.values}
                onResult={setPreview}
                onError={setPreviewError}
              />

              <Row className={isMobile ? "" : "mb-3"}>
                <Col xs={12} md={6} lg={4}>
                  <Label className="mb-0 asteric">Name of vessel</Label>
                  <Field
                    type="text"
                    id="daily_name_of_vessel"
                    name="name_of_vessel"
                    className="form-control mb-2"
                  />
                  {props.errors?.name_of_vessel && (
                    <Label className="error">{props.errors.name_of_vessel}</Label>
                  )}
                </Col>
                <Col xs={12} md={6} lg={4}>
                  <Label className="mb-0 asteric">IMO number</Label>
                  <Field
                    type="text"
                    id="daily_imo_number"
                    name="imo_number"
                    className="form-control mb-2"
                  />
                  {props.errors?.imo_number && (
                    <Label className="error">{props.errors.imo_number}</Label>
                  )}
                </Col>
                <Col xs={12} md={12} lg={4}>
                  <Label className="mb-0 asteric">Reporting year</Label>
                  <Field name="year">
                    {({ field, form }: FieldProps) => (
                      <Select
                        options={yearOptions}
                        value={yearOptions.find(
                          (option) => option.value === field.value
                        )}
                        onChange={(option: any) =>
                          form.setFieldValue(field.name, option?.value ?? null)
                        }
                        menuPortalTarget={document.body}
                        className="mb-2"
                        placeholder="Reporting year"
                      />
                    )}
                  </Field>
                  {props.errors?.year && (
                    <Label className="error">{props.errors.year}</Label>
                  )}
                </Col>
              </Row>

              {/* ------------------------------------------------ the mode */}
              <h6 className="mt-4 mb-1">What are you planning?</h6>
              <p className="fueleu-hint">
                Two questions, and only one of them applies at a time. Pick the
                one you are answering.
              </p>
              <div className="fueleu-modes mb-3">
                <Button
                  type="button"
                  className={`btn fueleu-mode${
                    !partial ? " fueleu-mode--on" : ""
                  }`}
                  onClick={() => props.setFieldValue("mode", MODE_FULL)}
                >
                  <span className="fueleu-mode__title">Plan from day one</span>
                  <span className="fueleu-mode__note">
                    Nothing burnt yet. One horizon, planned end to end.
                  </span>
                </Button>
                <Button
                  type="button"
                  className={`btn fueleu-mode${
                    partial ? " fueleu-mode--on" : ""
                  }`}
                  onClick={() => props.setFieldValue("mode", MODE_PARTIAL)}
                >
                  <span className="fueleu-mode__title">
                    Part of the period already burnt
                  </span>
                  <span className="fueleu-mode__note">
                    Enter what has gone, then plan the days ahead.
                  </span>
                </Button>
              </div>

              {!partial ? (
                <>
                  {/* ------------------------------------------- the horizon */}
                  <h6 className="mt-4 mb-1">Planning horizon</h6>
                  <p className="fueleu-hint">
                    How far ahead to plan. FuelEU scores the calendar year, so
                    the plan stops at {MAX_DAYS} days &mdash; a longer horizon
                    would be accruing one balance across two periods with two
                    different targets.
                  </p>
                  <Row className={isMobile ? "" : "mb-3"}>
                    <Col xs={12} md={6} lg={4}>
                      <div className="input-group mb-2">
                        <Field
                          type="text"
                          inputMode="numeric"
                          id="days"
                          name="days"
                          className="form-control"
                        />
                        <div className="input-group-text">days</div>
                      </div>
                      {props.errors?.days && (
                        <Label className="error d-block">
                          {props.errors.days}
                        </Label>
                      )}
                    </Col>
                    <Col xs={12} md={6} lg={8}>
                      {/* A month, a quarter, a half and the year — the horizons
                          anyone actually plans to, so they are one click rather
                          than a number to work out. */}
                      <div className="fueleu-presets mb-2">
                        {DAY_PRESETS.map((days) => (
                          <Button
                            key={days}
                            type="button"
                            className={`btn fueleu-preset${
                              Number(props.values.days) === days
                                ? " fueleu-preset--on"
                                : ""
                            }`}
                            onClick={() => props.setFieldValue("days", days)}
                          >
                            {formatDays(days)}
                          </Button>
                        ))}
                      </div>
                    </Col>
                  </Row>
                </>
              ) : (
                <>
                  {/* ----------------------------------- what has been burnt */}
                  <h6 className="mt-4 mb-1">
                    1. How much of the period has already been burnt?
                  </h6>
                  <p className="fueleu-hint">
                    The balance is still scored over the whole period &mdash;
                    FuelEU has no concept of a part-year &mdash; but the biofuel
                    can only displace fossil fuel in the days that are left, so
                    the same tonnage has to be bunkered in a shorter window.
                  </p>
                  <Row className={isMobile ? "" : "mb-3"}>
                    <Col xs={12} md={6} lg={4}>
                      <Label className="mb-0 asteric">
                        Days already consumed
                      </Label>
                      <div className="input-group mb-2">
                        <Field name="elapsed_days">
                          {({ field }: FieldProps) => (
                            <input
                              type="text"
                              inputMode="numeric"
                              id="elapsed_days"
                              className="form-control"
                              value={field.value ?? ""}
                              onChange={(e) =>
                                setElapsed(props, e.target.value)
                              }
                            />
                          )}
                        </Field>
                        <div className="input-group-text">days</div>
                      </div>
                      {props.errors?.elapsed_days && (
                        <Label className="error d-block">
                          {props.errors.elapsed_days}
                        </Label>
                      )}
                    </Col>
                    <Col xs={12} md={6} lg={8}>
                      <Label className="mb-0 d-none d-lg-block">&nbsp;</Label>
                      <div className="fueleu-presets mb-2">
                        {ELAPSED_PRESETS.map((days) => (
                          <Button
                            key={days}
                            type="button"
                            className={`btn fueleu-preset${
                              elapsedDays === days ? " fueleu-preset--on" : ""
                            }`}
                            onClick={() => setElapsed(props, days)}
                          >
                            {formatDays(days)}
                          </Button>
                        ))}
                      </div>
                    </Col>
                  </Row>

                  <p className="fueleu-hint">
                    Totals for the whole {formatDays(elapsedDays)}, not a daily
                    rate &mdash; a total is what a ship&rsquo;s records give you.
                    EU-scoped, on the same terms as the planned burn below.
                  </p>
                  <FuelMixFields
                    form={props}
                    factorsById={factorsById}
                    fuelGroups={fuelGroups}
                    blendFossilOptions={blendFossilOptions}
                    isCompact={isCompact}
                    unitLabel="mt"
                    quantityLabel="Total burnt"
                    quantityWholeDigits={MAX_QUANTITY_DIGITS}
                    arrayName="consumed_fuels"
                  />
                  {typeof props.errors?.consumed_fuels === "string" && (
                    <Label className="error d-block mt-2">
                      {props.errors.consumed_fuels}
                    </Label>
                  )}
                  <OpeningBalance result={preview} />

                  {/* --------------------------------- the forward window */}
                  <h6 className="mt-4 mb-1">
                    2. How far ahead do you want to plan?
                  </h6>
                  <p className="fueleu-hint">
                    Counted from the day after the stretch above. Planning to the
                    end of the year is what the compliance balance is actually
                    scored on; a shorter window answers &ldquo;where will I be by
                    then&rdquo;.
                  </p>
                  <Row className={isMobile ? "" : "mb-3"}>
                    <Col xs={12} md={6} lg={4}>
                      <Label className="mb-0 asteric">Plan the next</Label>
                      <div className="input-group mb-2">
                        <Field
                          type="text"
                          inputMode="numeric"
                          id="plan_days"
                          name="plan_days"
                          className="form-control"
                        />
                        <div className="input-group-text">days</div>
                      </div>
                      {props.errors?.plan_days && (
                        <Label className="error d-block">
                          {props.errors.plan_days}
                        </Label>
                      )}
                    </Col>
                    <Col xs={12} md={6} lg={8}>
                      <Label className="mb-0 d-none d-lg-block">&nbsp;</Label>
                      <div className="fueleu-presets mb-2">
                        {PLAN_PRESETS.filter(
                          (days) => elapsedDays + days <= MAX_DAYS
                        ).map((days) => (
                          <Button
                            key={days}
                            type="button"
                            className={`btn fueleu-preset${
                              planDays === days ? " fueleu-preset--on" : ""
                            }`}
                            onClick={() =>
                              props.setFieldValue("plan_days", days)
                            }
                          >
                            {formatDays(days)}
                          </Button>
                        ))}
                        {/* The one everybody actually wants, and the only window
                            the regulation scores. Labelled with the number so it
                            is not a mystery button. */}
                        <Button
                          type="button"
                          className={`btn fueleu-preset${
                            planDays === restOfYear ? " fueleu-preset--on" : ""
                          }`}
                          onClick={() =>
                            props.setFieldValue("plan_days", restOfYear)
                          }
                        >
                          Rest of the year ({restOfYear})
                        </Button>
                      </div>
                    </Col>
                  </Row>
                </>
              )}

              {/* The two numbers turned back into one sentence. Which days are
                  fixed and which are being planned is the whole shape of this
                  form, and it was the thing a reader had to assemble for
                  themselves out of two boxes that each said "days". */}
              <p className="fueleu-span">
                {partial ? (
                  elapsedDays >= 1 && planDays >= 1 ? (
                    <>
                      Days <strong>1&ndash;{elapsedDays}</strong> already burnt
                      &middot; planning days{" "}
                      <strong>
                        {elapsedDays + 1}&ndash;{horizonDays}
                      </strong>{" "}
                      &middot; <strong>{formatDays(horizonDays)}</strong> scored
                      in total
                    </>
                  ) : (
                    <>Enter both windows to see the period this plan covers.</>
                  )
                ) : (
                  <>
                    Planning <strong>{formatDays(horizonDays)}</strong> from day
                    one
                  </>
                )}
              </p>

              {/* ------------------------------------------- the daily burn */}
              <h6 className="mt-4 mb-1">
                {partial
                  ? `3. Planned daily fuel consumption for those ${formatDays(
                      remainingDays
                    )}`
                  : "Estimated daily fuel consumption"}
              </h6>
              <p className="fueleu-scope">
                Ensure you enter only the part of daily fuel applicable to the EU
                compliance.
                <span className="fueleu-scope__formula">
                  (FCvoyage × 100% or 50% + FCport × 100% or 50%)
                </span>
              </p>

              <FuelMixFields
                form={props}
                factorsById={factorsById}
                fuelGroups={fuelGroups}
                blendFossilOptions={blendFossilOptions}
                isCompact={isCompact}
                unitLabel="mt/day"
                quantityLabel="Quantity per day"
                quantityWholeDigits={MAX_DAILY_QUANTITY_DIGITS}
              />

              <DailyTotals result={preview} />

              {typeof props.errors?.fuels === "string" && (
                <Label className="error d-block mt-2">
                  {props.errors.fuels}
                </Label>
              )}

              {/* ----------------------------------------- zero-carbon energy */}
              <h6 className="mt-4 mb-1">OPS used per day (optional)</h6>
              <p className="fueleu-hint">
                Electricity delivered to the ship per OPS connection, averaged
                over the days in the plan.
              </p>
              <Row className={isMobile ? "" : "mb-3"}>
                <Col xs={12} md={6} lg={4}>
                  <Label className="mb-0">On-shore power supply (OPS)</Label>
                  <QuantityField
                    name="ops_energy_kwh_per_day"
                    unit="kWh/day"
                    maxWholeDigits={MAX_OPS_DIGITS}
                    maxDecimals={MAX_OPS_DECIMALS}
                  />
                </Col>
              </Row>

              {/* --------------------------------------------- the biofuel */}
              <h6 className="mt-4 mb-1">Biofuel to plan with</h6>
              <p className="fueleu-hint">
                The fuel the requirement is quoted in. The calculator solves for
                the quantity that replaces enough fossil energy to hold the
                accrued balance at zero.
              </p>
              <Row className={isMobile ? "" : "mb-3"}>
                <Col xs={12} md={6} lg={5}>
                  <Label className="mb-0">Biofuel</Label>
                  <Field name="alternative_fuel">
                    {({ field, form }: FieldProps) => {
                      const chosen = field.value
                        ? factorsById.get(field.value)
                        : undefined;
                      return (
                        <Select
                          options={alternativeGroups}
                          value={chosen ?? null}
                          getOptionLabel={(option: any) => option.label}
                          getOptionValue={(option: any) => String(option.id)}
                          onChange={(option: any) => {
                            form.setFieldValue(field.name, option?.id ?? null);
                            form.setFieldValue(
                              "alternative_intensity_override",
                              ""
                            );
                            form.setFieldValue("alternative_lcv_override", "");
                            form.setFieldValue("alternative_e_value", "");
                          }}
                          isClearable
                          menuPortalTarget={document.body}
                          className="mb-2"
                          placeholder="Select a biofuel"
                        />
                      );
                    }}
                  </Field>
                </Col>
                {needsOverride(alternativeFactor) && (
                  <>
                    <Col xs={12} md={6} lg={4}>
                      {usesEValue(alternativeFactor) ? (
                        <>
                          <Label className="mb-0 asteric">
                            E value (RED II Annex V)
                          </Label>
                          <QuantityField
                            name="alternative_e_value"
                            placeholder="e.g. 35"
                            unit="gCO₂eq/MJ"
                            maxWholeDigits={MAX_E_VALUE_DIGITS}
                            maxDecimals={MAX_E_VALUE_DECIMALS}
                          />
                          {props.submitCount > 0 &&
                            props.errors?.alternative_e_value && (
                              <Label className="error d-block">
                                {props.errors.alternative_e_value}
                              </Label>
                            )}
                        </>
                      ) : (
                        <>
                          <Label className="mb-0 asteric">
                            Certified GHG intensity
                          </Label>
                          <div className="input-group mb-2">
                            <Field
                              type="text"
                              inputMode="decimal"
                              name="alternative_intensity_override"
                              className="form-control"
                              placeholder="e.g. 20"
                            />
                            <div className="input-group-text">gCO₂eq/MJ</div>
                          </div>
                          {props.submitCount > 0 &&
                            props.errors?.alternative_intensity_override && (
                              <Label className="error d-block">
                                {props.errors.alternative_intensity_override}
                              </Label>
                            )}
                        </>
                      )}
                    </Col>
                    <Col xs={12} md={6} lg={3}>
                      <Label
                        className={`mb-0${
                          alternativeFactor?.lcv ? "" : " asteric"
                        }`}
                      >
                        {usesEValue(alternativeFactor)
                          ? "LCV (RED II Annex III)"
                          : "LCV"}
                      </Label>
                      <QuantityField
                        name="alternative_lcv_override"
                        placeholder="e.g. 0.044"
                        unit="MJ/g"
                        maxWholeDigits={MAX_LCV_DIGITS}
                        maxDecimals={MAX_LCV_DECIMALS}
                      />
                      <LcvEcho value={props.values.alternative_lcv_override} />
                      {props.submitCount > 0 &&
                        props.errors?.alternative_lcv_override && (
                          <Label className="error d-block">
                            {props.errors.alternative_lcv_override}
                          </Label>
                        )}
                    </Col>
                  </>
                )}
              </Row>

              {/* ---------------------------------------------- live figures */}
              <DailyLivePreview result={preview} error={previewError} />

              <Row>
                <Col>
                  <div className="form-actions mt-4">
                    <Button className="btn btn-primary" type="submit">
                      Calculate
                    </Button>
                    <Button
                      className="btn btn-primary"
                      type="reset"
                      onClick={() => {
                        props.resetForm({ values: initialValues });
                        setPreview(null);
                        setPreviewError("");
                      }}
                    >
                      Reset
                    </Button>
                  </div>
                </Col>
              </Row>

              {modalState && (
                <FuelEUDailyResultsModal
                  state={modalState}
                  setState={setModalState}
                  id={recordId}
                  result={savedResult}
                  vesselName={props.values.name_of_vessel}
                  year={props.values.year}
                />
              )}
            </Form>
          );
        }}
      </Formik>
    </CardBody>
  );
};

/**
 * Set the elapsed stretch, pulling the forward window back if it no longer fits.
 *
 * The two boxes have to sum to a year at most. Left alone, moving the stretch
 * from 90 to 300 days with a 275-day window already chosen would put the form
 * into an error state over a number the operator never touched — so the window
 * gives way, and the sentence under the two boxes shows what it gave way to.
 *
 * Only ever shrinks it. Growing the window because the stretch got shorter would
 * silently plan further ahead than was asked for.
 */
const setElapsed = (form: any, value: any) => {
  form.setFieldValue("elapsed_days", value);

  const elapsed = Number(value);
  const plan = Number(form.values.plan_days);
  if (!Number.isFinite(elapsed) || !Number.isFinite(plan)) return;

  const room = MAX_DAYS - elapsed;
  if (room >= 1 && plan > room) form.setFieldValue("plan_days", room);
};

/**
 * Where the balance stands the moment the plan takes over.
 *
 * The position the remaining days inherit, and the number that makes the split
 * worth making: it is fixed, it cannot be improved, and everything the plan does
 * from here has to pay it off on top of its own emissions. Shown directly under
 * the already-consumed table, because that table is what produced it.
 */
const OpeningBalance = ({ result }: { result: FuelEUDailyResult | null }) => {
  const summary = result?.summary;
  if (!summary || summary.opening_balance === null) return null;

  // An opening deficit is the bad news on this panel and the green rule of a
  // `.fueleu-mix` box reads as reassurance, so the box takes the verdict's
  // colour rather than the section's.
  const deficit = summary.opening_balance < 0;

  return (
    <div className={`fueleu-mix${deficit ? " fueleu-mix--deficit" : ""}`}>
      <div className="fueleu-mix__figure">
        <span className="fueleu-mix__label">
          Balance already banked after {formatDays(summary.elapsed_days)}
        </span>
        <span className="fueleu-mix__value">
          {formatBalance(summary.opening_balance)}
          <em>tCO₂eq</em>
        </span>
      </div>
      <div className="fueleu-mix__split">
        Attained {formatNumber(summary.opening_intensity, 3)} gCO₂eq/MJ against a
        target of {formatNumber(summary.target_intensity, 3)}
        {deficit && (
          <>
            {" "}&mdash; worth {formatEuro(summary.opening_penalty_eur)} if the
            rest of the period changes nothing about it.
          </>
        )}
      </div>
      <div className="fueleu-mix__split">
        This is fixed. The {formatDays(summary.remaining_days)} below have to
        carry it.
      </div>
    </div>
  );
};

/**
 * What the daily rates add up to over the horizon.
 *
 * The form asks for tonnes per day; the compliance balance is computed over
 * tonnes for the period. Between those two numbers sits a multiplication by up
 * to 365, and a slipped decimal in a daily rate is invisible until it is shown
 * as the bunker it implies. This is that check: "12.00 mt/day over 365 days =
 * 4,380 mt" is a figure an operator recognises or does not.
 */
const DailyTotals = ({ result }: { result: FuelEUDailyResult | null }) => {
  if (!result || !result.fuel_energy) return null;

  const total = result.fuels.reduce(
    (sum, fuel) => sum + (Number(fuel.mass) || 0),
    0
  );

  return (
    <div className="fueleu-mix">
      <div className="fueleu-mix__figure">
        <span className="fueleu-mix__label">
          Fuel burnt over {formatDays(result.summary.days)}
        </span>
        <span className="fueleu-mix__value">
          {formatNumber(total, 1)}
          <em>mt</em>
        </span>
      </div>
      <div className="fueleu-mix__split">
        {result.fuels
          .map((fuel) => `${fuel.name}: ${formatNumber(fuel.mass, 1)} mt`)
          .join("   ·   ")}
      </div>
      <div className="fueleu-mix__split">
        Well-to-wake intensity of this mix{" "}
        <strong>{formatNumber(result.fuel_intensity, 3)}</strong> gCO₂eq/MJ
      </div>
      {result.has_unpriced_fuel && (
        <div className="fueleu-mix__warning">
          {result.unpriced_fuels === 1
            ? "One fuel"
            : `${result.unpriced_fuels} fuels`}{" "}
          in this mix cannot be priced — no published emission factors, and no E
          value or certified intensity entered. Left out of the plan entirely, so
          it describes less fuel than the ship burns.
        </div>
      )}
    </div>
  );
};

/** The four figures the form recomputes as you type. */
const DailyLivePreview = ({
  result,
  error,
}: {
  result: FuelEUDailyResult | null;
  error: string;
}) => {
  if (error) {
    return <div className="fueleu-live fueleu-live--error">{error}</div>;
  }
  if (!result || !result.summary?.total_energy) {
    return (
      <div className="fueleu-live fueleu-live--empty">
        Add a fuel and a quantity to see the plan.
      </div>
    );
  }

  const summary = result.summary;

  return (
    <div className="fueleu-live">
      <div className="fueleu-live__item">
        <span className="fueleu-live__label">Attained intensity</span>
        <span className="fueleu-live__value">
          {formatNumber(summary.attained_intensity, 3)}
          <em>gCO₂eq/MJ vs target {formatNumber(summary.target_intensity, 3)}</em>
        </span>
      </div>
      <div
        className={`fueleu-live__item fueleu-live__item--${balanceRatingClass(
          summary.status
        )}`}
      >
        <span className="fueleu-live__label">
          Balance by day {summary.days}
        </span>
        <span className="fueleu-live__value">
          {formatBalance(summary.compliance_balance)}
          <em>tCO₂eq</em>
        </span>
      </div>
      <div className="fueleu-live__item">
        <span className="fueleu-live__label">
          {result.alternative_fuel ?? "Biofuel"} required
        </span>
        <span className="fueleu-live__value">
          {formatRequirement(summary.required_alternative_mass)}
          <em>
            {summary.required_alternative_mass === null
              ? summary.requirement_reason ?? "—"
              : /* Over the days that are LEFT. With part of the period gone the
                   tonnage does not move but the window does, and the rate is
                   the half of that an operator can act on. */
                `${formatNumber(
                  summary.alternative_mass_per_day,
                  2
                )} mt/day over the remaining ${summary.remaining_days}`}
          </em>
        </span>
      </div>
      <div className="fueleu-live__item">
        <span className="fueleu-live__label">Annex IV penalty</span>
        <span className="fueleu-live__value">
          {formatEuro(summary.penalty_eur)}
          <em>
            {summary.is_compliant
              ? "No penalty on a surplus"
              : `${formatEuro(summary.penalty_per_day)} a day`}
          </em>
        </span>
      </div>
    </div>
  );
};

/**
 * Keeps the live figures in step with the form.
 *
 * The annual form's `PreviewSync`, on the same terms and for the same reasons:
 * the watch signature is derived from the payload that gets posted rather than
 * hand-listed, so a field added to one and not the other cannot silently stop
 * reaching the request; and the response is guarded by a sequence number so a
 * slow reply for older inputs cannot overwrite a fast one for the current set.
 */
const DailyPreviewSync = ({
  values,
  onResult,
  onError,
}: {
  values: any;
  onResult: (result: FuelEUDailyResult | null) => void;
  onError: (message: string) => void;
}) => {
  const latest = useRef(0);
  const payload = buildDailyPayload(values);
  // The two identity fields do not enter the calculation, and leaving them in
  // would re-fire the preview on every keystroke in the vessel name.
  const { name_of_vessel, imo_number, ...calculationInputs } = payload;
  const signature = JSON.stringify(calculationInputs);

  useEffect(() => {
    const payload = JSON.parse(signature);
    const days = Number(payload.days);
    const rows = payload.fuels ?? [];
    // An elapsed stretch with nothing entered against it is not a half-filled
    // plan, it is a plan whose first N days burnt no fuel at all — zero energy,
    // zero intensity, and a balance that sits flat and then jumps. Better to
    // keep the empty state than to answer a question that was not asked.
    const consumedIfNeeded =
      !payload.elapsed_days ||
      rows.some((row: any) => row.phase === "consumed");
    const usable =
      rows.length > 0 &&
      consumedIfNeeded &&
      Number.isFinite(days) &&
      days >= 1 &&
      days <= MAX_DAYS &&
      // The API refuses an elapsed stretch that swallows the horizon, and it is
      // right to — but a 400 surfaces here as "could not reach the calculator",
      // which is the wrong thing to say about a number that is simply not
      // finished being typed.
      (payload.elapsed_days ?? 0) < days;

    if (!usable) {
      onResult(null);
      onError("");
      return;
    }

    const sequence = ++latest.current;
    const timer = setTimeout(() => {
      postDailyPreview(payload)
        .then((result) => {
          if (sequence !== latest.current) return;
          onResult(result);
          onError("");
        })
        .catch(() => {
          if (sequence !== latest.current) return;
          onError("Could not reach the calculator. Check your connection.");
        });
    }, PREVIEW_DEBOUNCE_MS);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature]);

  return null;
};

export default FuelEUDailyCalculator;
